import { formatIsoDate, parseIsoDate } from "./shipByDatePickerUtils";

const HOLIDAY_NAMES: Record<string, string> = {
  new_years_day: "New Year's Day",
  mlk_day: "Martin Luther King Jr. Day",
  presidents_day: "Presidents' Day",
  good_friday: "Good Friday",
  memorial_day: "Memorial Day",
  juneteenth: "Juneteenth",
  independence_day: "Independence Day",
  labor_day: "Labor Day",
  thanksgiving: "Thanksgiving",
  day_after_thanksgiving: "Day after Thanksgiving",
  christmas_eve: "Christmas Eve",
  christmas: "Christmas",
  new_years_eve: "New Year's Eve",
};

function observeFixedHoliday(year: number, monthIndex: number, day: number): string {
  const date = new Date(year, monthIndex, day, 12, 0, 0, 0);
  const weekday = date.getDay();
  if (weekday === 6) {
    date.setDate(date.getDate() - 1);
  } else if (weekday === 0) {
    date.setDate(date.getDate() + 1);
  }
  return formatIsoDate(date);
}

function nthWeekdayOfMonth(year: number, monthIndex: number, weekday: number, occurrence: number): string {
  const first = new Date(year, monthIndex, 1, 12, 0, 0, 0);
  let offset = (weekday - first.getDay() + 7) % 7;
  offset += (occurrence - 1) * 7;
  const date = new Date(year, monthIndex, 1 + offset, 12, 0, 0, 0);
  return formatIsoDate(date);
}

function lastWeekdayOfMonth(year: number, monthIndex: number, weekday: number): string {
  const lastDay = new Date(year, monthIndex + 1, 0, 12, 0, 0, 0);
  const diff = (lastDay.getDay() - weekday + 7) % 7;
  lastDay.setDate(lastDay.getDate() - diff);
  return formatIsoDate(lastDay);
}

function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function goodFriday(year: number): string {
  const easter = easterSunday(year);
  const friday = new Date(easter);
  friday.setDate(easter.getDate() - 2);
  return formatIsoDate(friday);
}

function juneteenthIfWeekday(year: number): string | null {
  const date = new Date(year, 5, 19, 12, 0, 0, 0);
  const weekday = date.getDay();
  if (weekday === 0 || weekday === 6) {
    return null;
  }
  return formatIsoDate(date);
}

function holidaysForYear(year: number): Map<string, string> {
  const byDate = new Map<string, string>();
  const add = (isoDate: string, key: keyof typeof HOLIDAY_NAMES) => {
    byDate.set(isoDate, HOLIDAY_NAMES[key]);
  };

  add(observeFixedHoliday(year, 0, 1), "new_years_day");
  add(nthWeekdayOfMonth(year, 0, 1, 3), "mlk_day");
  add(nthWeekdayOfMonth(year, 1, 1, 3), "presidents_day");
  add(goodFriday(year), "good_friday");
  add(lastWeekdayOfMonth(year, 4, 1), "memorial_day");
  const juneteenth = juneteenthIfWeekday(year);
  if (juneteenth != null) {
    add(juneteenth, "juneteenth");
  }
  add(observeFixedHoliday(year, 6, 4), "independence_day");
  add(nthWeekdayOfMonth(year, 8, 1, 1), "labor_day");
  const thanksgiving = nthWeekdayOfMonth(year, 10, 4, 4);
  add(thanksgiving, "thanksgiving");
  const parsedThanksgiving = parseIsoDate(thanksgiving);
  if (parsedThanksgiving != null) {
    const dayAfter = new Date(parsedThanksgiving);
    dayAfter.setDate(dayAfter.getDate() + 1);
    add(formatIsoDate(dayAfter), "day_after_thanksgiving");
  }
  add(formatIsoDate(new Date(year, 11, 24, 12, 0, 0, 0)), "christmas_eve");
  add(observeFixedHoliday(year, 11, 25), "christmas");
  add(formatIsoDate(new Date(year, 11, 31, 12, 0, 0, 0)), "new_years_eve");

  return byDate;
}

const cache = new Map<number, Map<string, string>>();

function holidayMapForIsoYear(isoDate: string): Map<string, string> | null {
  const parsed = parseIsoDate(isoDate);
  if (parsed == null) {
    return null;
  }
  const year = parsed.getFullYear();
  let map = cache.get(year);
  if (map == null) {
    map = holidaysForYear(year);
    cache.set(year, map);
  }
  return map;
}

export function getSgsHolidayName(isoDate: string): string | null {
  const trimmed = isoDate.trim().slice(0, 10);
  if (trimmed.length !== 10) {
    return null;
  }
  const map = holidayMapForIsoYear(trimmed);
  return map?.get(trimmed) ?? null;
}

export function sgsHolidayFieldSuffix(isoDate: string): string | null {
  const name = getSgsHolidayName(isoDate);
  if (name == null) {
    return null;
  }
  return `${name} (SGS holiday)`;
}
